import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MoreVertical, type LucideIcon } from 'lucide-react';

export interface ActionItem {
  label: string;
  icon?: LucideIcon;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
}

interface ActionMenuProps {
  items: ActionItem[];
  /** Accessible label for the trigger button. */
  label?: string;
}

const MENU_WIDTH = 208; // w-52

/**
 * Compact "more actions" dropdown. The panel is rendered in a portal and
 * positioned from the trigger's bounding rect so it isn't clipped by ancestors
 * with `overflow: hidden` (e.g. the products table). Closes on outside click,
 * Escape, scroll, resize, and item selection.
 */
export function ActionMenu({ items, label = 'Mais ações' }: ActionMenuProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const place = (): void => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    // Right-align the panel to the trigger.
    const left = Math.max(8, Math.min(r.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - 8));
    // Flip above the trigger when there isn't room below (e.g. last table row).
    const estHeight = items.length * 40 + 8;
    const openUp = r.bottom + 4 + estHeight > window.innerHeight - 8 && r.top > estHeight;
    const top = openUp ? r.top - 4 - estHeight : r.bottom + 4;
    setPos({ top, left });
  };

  useLayoutEffect(() => {
    if (open) place();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent): void => {
      const target = e.target as Node;
      if (menuRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onReflow = (): void => setOpen(false);
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onReflow, true);
    window.addEventListener('resize', onReflow);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onReflow, true);
      window.removeEventListener('resize', onReflow);
    };
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-admin-border bg-white text-admin-text-soft transition-colors hover:bg-admin-surface-alt"
      >
        <MoreVertical aria-hidden className="h-4 w-4" />
      </button>
      {open
        ? createPortal(
            <div
              ref={menuRef}
              role="menu"
              style={{ top: pos.top, left: pos.left, width: MENU_WIDTH }}
              className="fixed z-50 overflow-hidden rounded-md border border-admin-border bg-white py-1 shadow-lg"
            >
              {items.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.label}
                    type="button"
                    role="menuitem"
                    disabled={item.disabled}
                    onClick={() => {
                      setOpen(false);
                      item.onClick();
                    }}
                    className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors disabled:opacity-50 ${
                      item.danger
                        ? 'text-admin-danger hover:bg-admin-danger/10'
                        : 'text-admin-text hover:bg-admin-surface-alt'
                    }`}
                  >
                    {Icon ? <Icon aria-hidden className="h-4 w-4 shrink-0" /> : null}
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </div>,
            document.body
          )
        : null}
    </>
  );
}
